import {
  all,
  AllEffect,
  ForkEffect,
  put,
  PutEffect,
  select,
  SelectEffect,
  take,
  TakeEffect,
  takeLatest,
} from 'redux-saga/effects';
import { PRODUCT_BOX_STYLE_TYPE } from 'b2b-components/theme/components/ProductList';
import {
  ANALYZE_SEARCH_EVENT,
  ANALYZE_SEARCH_FILTER_CHANGE,
  ANALYZE_SEARCH_PAGE_VIEW,
  ANALYZE_SIMILAR_SEARCH_QUERY,
  ANALYZE_SUGGESTED_CATEGORY_SEARCH,
  ANALYZE_SUGGESTED_SEARCH_RESULT_PRODUCT_CLICK,
  AnalyzeSearchFilterChangeAction,
  AnalyzeSuggestedCategorySearchAction,
  AnalyzeSuggestedSearchResultProductClickAction,
  getResultsPerPageFromSearchData,
  getSearchAnalyticsData,
  OSS_INTERACTION_LABEL,
  OSS_INTERACTION_PARAMETERS,
  SEARCH_COMPONENT_CATEGORY,
} from 'b2b-apps/shop/analytics/redux';
import { analyzeEvent, analyzeView } from 'b2b-apps/shop/analytics/redux/tealium/actionCreators';
import {
  TEALIUM_INTERACTION_ID,
  TEALIUM_PAGE_TYPE,
  TEALIUM_PROD_ACTION,
  TEALIUM_PROD_LIST,
  ZERO,
} from 'b2b-apps/shop/analytics/types/types';
import { FETCH_CATALOG_SEARCH_FULFILLED } from 'b2b-apps/shop/catalog/redux/search/actionTypes';
import { getSearch } from 'b2b-apps/shop/catalog/redux/search/selectors';
import { CatalogSearchType, FetchCatalogSearchFulfilledAction } from 'b2b-apps/shop/catalog/redux/search/types';
import { getSuggestions } from 'b2b-apps/shop/catalog/redux/suggestions/selectors';
import { SuggestionsType } from 'b2b-apps/shop/catalog/redux/suggestions/types';
import { RemoteRecord } from 'b2b-apps/shop/common/redux/RemoteRecord';
import { getProductsTiles } from 'b2b-apps/shop/product/redux/products/tile/selectors';
import { ProductTileType } from 'b2b-apps/shop/product/redux/products/tile/types';
import { getCatalogSettings } from 'b2b-apps/shop/settings/redux/selectors';
import { SettingsCatalogType } from 'b2b-apps/shop/settings/redux/types';

function* analyzeSearchEventSaga(): Generator<
  PutEffect | TakeEffect | SelectEffect,
  void,
  FetchCatalogSearchFulfilledAction & SettingsCatalogType
  > {
  const fetchCatalogSearchFulfilledAction = yield take(FETCH_CATALOG_SEARCH_FULFILLED);

  if (fetchCatalogSearchFulfilledAction) {
    const { requestedQuery } = fetchCatalogSearchFulfilledAction.payload.data;

    yield put(analyzeEvent(
      getSearchAnalyticsData({
        id: TEALIUM_INTERACTION_ID.searchStart,
        query: requestedQuery,
      }),
    ));
  }
}

function* analyzeSearchPageViewSaga(): Generator<
  PutEffect | SelectEffect,
  void,
  RemoteRecord<CatalogSearchType>
  & Record<string, RemoteRecord<ProductTileType>>
  & SettingsCatalogType
  > {
  const search: RemoteRecord<CatalogSearchType> = yield select(getSearch);
  const productTiles: Record<string, RemoteRecord<ProductTileType>> = yield select(getProductsTiles);
  const { sorting, viewType } = yield select(getCatalogSettings);
  const {
    site,
    productsCount,
    query,
    requestedQuery,
    products: productsSkus,
  } = search.data;
  const { page } = site;
  const products = productsSkus
    .filter(product => productTiles[product.sku]?.data)
    .map((product, index) => ({
      ...productTiles[product.sku].data,
      position: index,
    }));
  const originalQuery = query !== requestedQuery
    ? requestedQuery : null;

  yield put(analyzeView(
    getSearchAnalyticsData({
      id: TEALIUM_INTERACTION_ID.searchPageView,
      pageType: TEALIUM_PAGE_TYPE.productSearch,
      query,
      originalQuery,
      resultsPerPage: getResultsPerPageFromSearchData(search.data),
      page,
      productsCount,
      listType: viewType || PRODUCT_BOX_STYLE_TYPE.list,
      products,
      productsAction: TEALIUM_PROD_ACTION.productsList,
      productsList: TEALIUM_PROD_LIST.search,
      sortType: sorting || 'rankasc',
    }),
  ));
}

function* analyzeSuggestedSearchResultProductClickSaga(
  action: AnalyzeSuggestedSearchResultProductClickAction,
): Generator<PutEffect | SelectEffect, void, RemoteRecord<SuggestionsType>> {
  const suggestions: RemoteRecord<SuggestionsType> = yield select(getSuggestions);
  const product = action.payload.product;
  const products = suggestions.data.products;

  yield put(analyzeEvent({
    interaction: TEALIUM_INTERACTION_ID.suggestedProductClick,
    prod: {
      action: TEALIUM_PROD_ACTION.productListClick,
      categories: {},
      pos: product.position,
      price: product.effectivePricing.mainPrice,
      quan: 1,
      list: [TEALIUM_PROD_LIST.suggestion],
    },
    oss: {
      ...OSS_INTERACTION_PARAMETERS[TEALIUM_INTERACTION_ID.suggestedProductClick],
      resultCount: products?.length || ZERO,
      term: suggestions?.data?.term?.toLowerCase(),
      type: TEALIUM_PROD_LIST.suggestion,
    },
  }));
}

function* analyzeSimilarSearchQuerySaga(): Generator<PutEffect | SelectEffect, void, RemoteRecord<SuggestionsType>> {
  const suggestions: RemoteRecord<SuggestionsType> = yield select(getSuggestions);
  const suggestedTerms = suggestions.data.suggestions;

  yield put(analyzeEvent({
    interaction: TEALIUM_INTERACTION_ID.similarQueryClick,
    oss: {
      ...OSS_INTERACTION_PARAMETERS[TEALIUM_INTERACTION_ID.similarQueryClick],
      resultCount: suggestedTerms?.length || ZERO,
      term: suggestions?.data?.term?.toLowerCase(),
      type: TEALIUM_PROD_LIST.search,
    },
  }));
}

function* analyzeSuggestedCategorySearchSaga(
  action: AnalyzeSuggestedCategorySearchAction,
): Generator<PutEffect | SelectEffect, void, RemoteRecord<SuggestionsType>> {
  const suggestions: RemoteRecord<SuggestionsType> = yield select(getSuggestions);
  const facets = suggestions.data.facets.facet;

  yield put(analyzeEvent({
    interaction: TEALIUM_INTERACTION_ID.suggestedCategoriesSearchStart,
    component: {
      attribute1: [action.payload.facetName],
    },
    oss: {
      ...OSS_INTERACTION_PARAMETERS[TEALIUM_INTERACTION_ID.suggestedCategoriesSearchStart],
      resultCount: facets?.length || ZERO,
      term: suggestions?.data?.term?.toLowerCase(),
      type: TEALIUM_PROD_LIST.search,
    },
  }));
}

function* analyzeSearchFilterChangeSaga(
  action: AnalyzeSearchFilterChangeAction,
): Generator<PutEffect | SelectEffect, void, RemoteRecord<CatalogSearchType>> {
  const search: RemoteRecord<CatalogSearchType> = yield select(getSearch);
  const filterValue = action.payload.filterValue;

  yield put(analyzeEvent({
    interaction: TEALIUM_INTERACTION_ID.searchFilterClick,
    component: {
      attribute1: [action.payload.filterName],
      ...filterValue && { attribute2: [filterValue] },
      attribute3: [action.payload.filterAction],
      category: [SEARCH_COMPONENT_CATEGORY.srchFilter],
    },
    oss: {
      ...OSS_INTERACTION_PARAMETERS[TEALIUM_INTERACTION_ID.searchFilterClick],
      interactionLabel: `${OSS_INTERACTION_LABEL.filter}:${action.payload.filterName}`,
      resultCount: search?.data?.productsCount === 0 ? ZERO : search?.data?.productsCount,
      term: search?.data?.query?.toLowerCase(),
      type: TEALIUM_PROD_LIST.search,
    },
  }));
}

/*
* We are waiting for business decisions regarding this feature
* */
// function* analyzeFilterUsageSaga(
//   { payload }: AnalyzeProductFiltersAction,
// ): Generator<any, any, any> {
//   const {
//     filterType,
//     filters = {},
//   } = payload;
//   if (Object.keys(filters).length) {
//     const catalogFilters = Object.entries(filters).map(([name, values]: any) => ({
//       name: values.name ? values.name : name,
//       value: values.value ? values.value : values,
//     }));
//
//     const data: FilterAnalyticsProps = {
//       component_category: ['product_finder'],
//       component_subcategory: [filterType],
//       component_attribute3: ['select_filter'],
//       product_finder_name: filterType,
//       product_finder_filters: [
//         ...catalogFilters,
//       ],
//     };
//     yield put(analyzeEvent(data));
//   }
// }

export function* searchAnalyticsSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    // takeLatest(ANALYZE_PRODUCT_FILTER, analyzeFilterUsageSaga),
    takeLatest(ANALYZE_SEARCH_EVENT, analyzeSearchEventSaga),
    takeLatest(ANALYZE_SEARCH_FILTER_CHANGE, analyzeSearchFilterChangeSaga),
    takeLatest(ANALYZE_SEARCH_PAGE_VIEW, analyzeSearchPageViewSaga),
    takeLatest(ANALYZE_SIMILAR_SEARCH_QUERY, analyzeSimilarSearchQuerySaga),
    takeLatest(ANALYZE_SUGGESTED_CATEGORY_SEARCH, analyzeSuggestedCategorySearchSaga),
    takeLatest(ANALYZE_SUGGESTED_SEARCH_RESULT_PRODUCT_CLICK, analyzeSuggestedSearchResultProductClickSaga),
  ]);
}
